import formatFilters from '@/utils/formatFilters'
import ADVANCED_FILTERS_TYPES from '@/constants/enums/ADVANCED_FILTERS_TYPES'

/**
 * Builds the request bodies for POST /quotes/kanban and POST /quotes/cards.
 *
 * Both endpoints answer 422 for the keys the table view sends, and neither
 * accepts the flat `statusesIn` parameter: the board selects its columns with
 * `statuses`, the card view goes through `filters.quote.statuses` like any
 * other filter.
 */

const STATUSES_FILTER_KEY = 'filters[quote][statuses]'

const KANBAN_PROHIBITED_KEYS = ['fields', 'statusesIn', 'page', 'per_page', 'with_pagination']
const CARDS_PROHIBITED_KEYS = ['fields', 'statusesIn']

type FlatFilters = Record<string, any>

type KanbanOptions = {
  statuses: string[],
  limit: number,
  offset: number,
}

const omitKeys = (filters: FlatFilters, keys: string[]): FlatFilters => {
  return Object.fromEntries(
    Object.entries(filters || {}).filter(([key]) => !keys.includes(key)),
  )
}

const toSlugList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter(Boolean)
  }

  if (typeof value === 'string' && value.length) {
    return value.split('|').filter(Boolean)
  }

  return []
}

export const buildKanbanRequest = (filters: FlatFilters, { statuses, limit, offset }: KanbanOptions) => {
  return {
    payload: {
      ...formatFilters.toObject(omitKeys(filters, KANBAN_PROHIBITED_KEYS)),
      statuses,
      limit,
      offset,
    },
  }
}

export const buildCardsRequest = (filters: FlatFilters) => {
  const payload = formatFilters.toObject(omitKeys(filters, CARDS_PROHIBITED_KEYS))
  const pillsStatuses = toSlugList(filters?.statusesIn)

  if (!pillsStatuses.length) {
    return { payload, isEmptyResult: false }
  }

  const advancedOption = filters?.[STATUSES_FILTER_KEY]?.option
  const advancedStatuses = toSlugList(filters?.[STATUSES_FILTER_KEY]?.value)

  let statuses: string[] = pillsStatuses

  if (advancedStatuses.length) {
    if (advancedOption === ADVANCED_FILTERS_TYPES.is) {
      statuses = pillsStatuses.filter((slug) => advancedStatuses.includes(slug))
    }
    else if (advancedOption === ADVANCED_FILTERS_TYPES.isNot) {
      statuses = pillsStatuses.filter((slug) => !advancedStatuses.includes(slug))
    }
  }

  // The pills and the advanced status filter share one key, so an empty
  // intersection cannot be expressed in the request: an empty value reads as
  // "no filter" and would return everything. The caller skips the request.
  if (!statuses.length) {
    return { payload, isEmptyResult: true }
  }

  payload.filters = {
    ...(payload.filters || {}),
    quote: {
      ...(payload.filters?.quote || {}),
      statuses: {
        option: ADVANCED_FILTERS_TYPES.is,
        value: statuses.join('|'),
      },
    },
  }

  return { payload, isEmptyResult: false }
}
